import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Table,
  Button,
  Row,
  Col,
  Select,
  notification,
  Space,
  InputNumber,
  Popconfirm,
  Typography,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import {
  getIndicadores,
  getPuestos,
  createIndicador,
  updateIndicador,
} from "../../actions/evaluacionDesempeño";
import "./modalIndicadores.css"; // Importar el archivo CSS

const { Option } = Select;

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  const inputNode =
    inputType === "number" ? (
      <InputNumber />
    ) : inputType === "select" ? (
      <Select className="truncated-select" style={{ width: '100%' }}>
        {record.puestos.map((puesto) => (
          <Option key={puesto.id} value={puesto.id} className="truncated-option">
            {puesto.nombre}
          </Option>
        ))}
      </Select>
    ) : (
      <Input />
    );
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{
            margin: 0,
          }}
          rules={[
            {
              required: true,
              message: `Please Input ${title}!`,
            },
          ]}
        >
          {inputNode}
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const ModalIndicadores = ({ visible, onClose }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  const [showForm, setShowForm] = useState(false); // Nuevo estado para mostrar/ocultar el formulario
  const [editingKey, setEditingKey] = useState("");

  // Estado global
  const { puestos, loading, error, indicadores } = useSelector(
    (state) => state.evaluacionDesempeño
  );

  // Cargar puestos e indicadores si no existen al abrir el modal
  useEffect(() => {
    if (visible && !puestos.length) {
      dispatch(getPuestos());
    }
    if (visible && !indicadores.length) {
      dispatch(getIndicadores());
    }
  }, [visible, dispatch, puestos, indicadores]);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div
        style={{
          padding: 8,
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <Input
          ref={searchInput}
          placeholder={`Buscar ${dataIndex}`}
          value={selectedKeys[0] || ""}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{
            marginBottom: 8,
            display: "block",
          }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{
              width: 90,
            }}
          >
            Buscar
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{
              width: 90,
            }}
          >
            Resetear
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({
                closeDropdown: false,
              });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filtrar
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            Cerrar
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "#ffc069",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const isEditing = (record) => record.key === editingKey;

  const edit = (record) => {
    form.setFieldsValue({
      nombre: record.nombre,
      valor: record.valor,
      min: record.min,
      sat: record.sat,
      exc: record.exc,
      puesto: record.puesto.id, // Establecer el valor inicial del puesto
    });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (key) => {
    try {
      const row = await form.validateFields();
      const newData = [...indicadores];
      const index = newData.findIndex((item) => key === item.key);
      if (index > -1) {
        const item = newData[index];
        const updatedItem = {
          id: item.id, // Asegurarse de incluir el id del registro en el objeto actualizado
          indicador: row.nombre,
          valor: row.valor,
          min: row.min,
          sat: row.sat,
          exc: row.exc,
          puestoId: row.puesto, // Solo enviar el id del puesto
        };
        newData.splice(index, 1, updatedItem);
        dispatch(updateIndicador(item.id, updatedItem)).then(() => {
          dispatch(getIndicadores()); // Recargar los indicadores después de actualizar
        });
        setEditingKey("");
      } else {
        const newItem = {
          id: key, // Asegurarse de incluir el id del registro en el nuevo objeto
          indicador: row.nombre,
          valor: row.valor,
          min: row.min,
          sat: row.sat,
          exc: row.exc,
          puestoId: row.puesto, // Solo enviar el id del puesto
        };
        newData.push(newItem);
        dispatch(updateIndicador(newItem.id, newItem)).then(() => {
          dispatch(getIndicadores()); // Recargar los indicadores después de actualizar
        });
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Nombre",
      dataIndex: "nombre",
      key: "nombre",
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      ...getColumnSearchProps("nombre"),
      editable: true,
    },
    {
      title: "Valor",
      dataIndex: "valor",
      key: "valor",
      editable: true,
    },
    {
      title: "Min",
      dataIndex: "min",
      key: "min",
      editable: true,
    },
    {
      title: "Sat",
      dataIndex: "sat",
      key: "sat",
      editable: true,
    },
    {
      title: "EXC",
      dataIndex: "exc",
      key: "exc",
      editable: true,
    },
    {
      title: "Puesto",
      dataIndex: "puesto",
      key: "puesto",
      sorter: (a, b) => a.puesto.nombre.localeCompare(b.puesto.nombre),
      filters: puestos.map((puesto) => ({
        text: puesto.nombre,
        value: puesto.id,
      })),
      filterSearch: true,
      onFilter: (value, record) => record.puesto.id === value,
      render: (puesto) => (
        <div className="truncated-text">{puesto.nombre}</div>
      ),
      editable: true,
    },
    {
      title: "operation",
      dataIndex: "operation",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          <span>
            <Button
              onClick={() => save(record.key)}
              type="primary"
              style={{
                marginRight: 8,
              }}
            >
              Guardar
            </Button>
            <Popconfirm
              title="¿Seguro que quieres cancelar?"
              onConfirm={cancel}
              okText="Sí, cancelar"
              cancelText="No ahora"
            >
              <Button>Cancelar</Button>
            </Popconfirm>
          </span>
        ) : (
          <Button
            type="link"
            disabled={editingKey !== ""}
            onClick={() => edit(record)}
          >
            Editar
          </Button>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record: { ...record, puestos },
        inputType:
          col.dataIndex === "valor" ||
          col.dataIndex === "min" ||
          col.dataIndex === "sat" ||
          col.dataIndex === "exc"
            ? "number"
            : col.dataIndex === "puesto"
            ? "select"
            : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  // Mapeo de indicadores para la tabla
  const data = indicadores.map((indicador) => ({
    key: indicador.id,
    id: indicador.id, // Asegurarse de incluir el id en los datos de la tabla
    nombre: indicador.indicador,
    valor: indicador.valor,
    min: indicador.min,
    sat: indicador.sat,
    exc: indicador.exc,
    puesto: indicador.puesto ? indicador.puesto : { nombre: "Sin puesto" },
  }));

  const numberValidator = (_, value) => {
    if (!value || value <= 0) {
      return Promise.reject(
        new Error("El valor debe ser un número positivo mayor que cero")
      );
    }
    return Promise.resolve();
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const data = {
          indicador: values.nombre,
          valor: values.valor,
          min: values.min,
          sat: values.sat,
          exc: values.exc,
          puestoId: values.puesto,
        };

        dispatch(createIndicador(data)).then((response) => {
          if (response.error === false) {
            notification.success({
              message: "Indicador creado correctamente",
              description: response.message,
              placement: "topRight",
            });
            dispatch(getIndicadores()); // Recargar los indicadores después de crear
            form.resetFields();
          } else {
            notification.error({
              message: "Error al crear el indicador",
              description: response.message,
              placement: "topRight",
            });
          }
          onClose();
        });
      })
      .catch((info) => console.log("Validación fallida:", info));
  };

  const handleNew = () => {
    form.resetFields();
    setShowForm(true);
  };

  if (loading) return <p>Cargando...</p>;
  if (error) return <p>Error al cargar los datos: {error.message}</p>;

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Administrar Indicadores</span>
          {!showForm && (
            <Button type="primary" onClick={handleNew} style={{ marginRight: 32 }}>
              Nuevo
            </Button>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={1200} // Ajusta el ancho del modal aquí
      footer={null}
    >
      {!showForm ? (
        <>
          <Form form={form} component={false}>
            <Table
              components={{
                body: {
                  cell: EditableCell,
                },
              }}
              bordered
              dataSource={data}
              columns={mergedColumns}
              rowClassName="editable-row"
              pagination={{
                onChange: cancel,
                pageSize: 5,
              }}
              style={{ marginTop: 24 }}
            />
          </Form>
        </>
      ) : (
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Indicador" name="nombre">
                <Input placeholder="Ingrese el nombre del indicador" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Valor"
                name="valor"
                rules={[
                  { required: true, message: "El valor es obligatorio" },
                  { validator: numberValidator },
                ]}
              >
                <Input
                  placeholder="Ingrese el valor del indicador"
                  type="number"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Min"
                name="min"
                rules={[
                  { required: true, message: "El valor mínimo es obligatorio" },
                  { validator: numberValidator },
                ]}
              >
                <Input placeholder="Min del indicador" type="number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Sat"
                name="sat"
                rules={[
                  { required: true, message: "El valor SAT es obligatorio" },
                  { validator: numberValidator },
                ]}
              >
                <Input placeholder="SAT del indicador" type="number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Exc"
                name="exc"
                rules={[
                  { required: true, message: "El valor EXC es obligatorio" },
                  { validator: numberValidator },
                ]}
              >
                <Input placeholder="EXC del indicador" type="number" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="Puesto" name="puesto">
                <Select
                  placeholder="Seleccione el puesto relacionado"
                  className="truncated-select"
                  style={{ width: '100%' }}
                >
                  {puestos.map((puesto) => (
                    <Option key={puesto.id} value={puesto.id} className="truncated-option">
                      {puesto.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={24} style={{ textAlign: "right" }}>
              <Button
                onClick={() => setShowForm(false)}
                style={{ marginRight: 8 }}
              >
                Cancelar
              </Button>
              <Button type="primary" onClick={handleOk}>
                Guardar
              </Button>
            </Col>
          </Row>
        </Form>
      )}
    </Modal>
  );
};

export default ModalIndicadores;